<template>
  <div class="min-h-screen bg-secondary-50 dark:bg-secondary-900">
    <div class="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <Link
          href="/"
          class="inline-flex items-center text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mb-4"
        >
          <ArrowLeftIcon class="h-5 w-5 mr-2" />
          Back to Home
        </Link>
        <h1 class="text-4xl font-bold text-secondary-900 dark:text-white mb-2">
          Privacy Policy
        </h1>
        <p class="text-secondary-600 dark:text-secondary-400">
          Last Updated: 4 November 2024
        </p>
      </div>

      <!-- Content -->
      <div class="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-8 space-y-8">
        <!-- Introduction -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">Introduction</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>
              Margin Solutions Ltd ("we", "us", "our") is committed to protecting and respecting your privacy.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you
              use Margin Resolve (the "Service"), our software-as-a-service platform.
            </p>
            <p class="mt-4">
              <strong>Company Information:</strong><br>
              Margin Resolve is operated by Margin Solutions Ltd<br>
              Company Registration Number: SC744893<br>
              Registered in: United Kingdom<br>
              Registered Office: Suite 3.10, 16 Robertson Street, Glasgow, Scotland, G2 8DS<br>
              Contact Email: <a href="mailto:info@marginresolve.com" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginresolve.com</a><br>
              General Enquiries: <a href="mailto:info@marginsolutions.co.uk" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginsolutions.co.uk</a>
            </p>
            <p class="mt-4">
              We are the data controller for the purposes of the UK General Data Protection Regulation (UK GDPR)
              and the Data Protection Act 2018. This policy applies to all users of our Service.
            </p>
            <p class="mt-4">
              Please read this Privacy Policy carefully. By accessing or using our Service, you acknowledge that you
              have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with this policy,
              please do not use our Service.
            </p>
          </div>
        </section>

        <!-- 1. Information We Collect -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">1. Information We Collect</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p><strong>1.1 Information You Provide to Us:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li><strong>Account Information:</strong> When you register for an account, we collect your name, email address, company name, phone number, and password.</li>
              <li><strong>Profile Information:</strong> Additional information you may provide such as job title, department, profile picture, and preferences.</li>
              <li><strong>Payment Information:</strong> Billing details including credit card information, billing address, and VAT number. Payment card data is processed securely by our third-party payment processor (Stripe) and is not stored on our servers.</li>
              <li><strong>Customer Data:</strong> All data, content, files, documents, and information you upload, create, or store within the Service, including tickets, incidents, work orders, asset information, maintenance records, and any other business data.</li>
              <li><strong>Communications:</strong> Information contained in communications you send to us, including support requests, feedback, and complaints.</li>
            </ul>
            <p class="mt-4"><strong>1.2 Information Collected Automatically:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li><strong>Usage Data:</strong> Information about how you access and use the Service, including features used, actions taken, time spent, and frequency of use.</li>
              <li><strong>Device Information:</strong> Information about the device you use to access the Service, including IP address, browser type and version, operating system, device type, and unique device identifiers.</li>
              <li><strong>Log Data:</strong> Server logs that include IP addresses, access times, pages viewed, error logs, and other diagnostic data.</li>
              <li><strong>Cookies and Similar Technologies:</strong> We use cookies, web beacons, and similar tracking technologies to collect information about your browsing activities. See Section 8 for more details.</li>
              <li><strong>Location Data:</strong> General geographic location based on your IP address (country/city level, not precise location).</li>
            </ul>
            <p class="mt-4"><strong>1.3 Information from Third Parties:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Information from payment processors regarding transaction status and payment verification.</li>
              <li>Information from authentication services if you choose to log in through third-party services.</li>
              <li>Publicly available information to verify company details during registration.</li>
            </ul>
          </div>
        </section>

        <!-- 2. Legal Basis for Processing -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">2. Legal Basis for Processing (UK GDPR)</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>We process your personal data on the following legal bases under UK GDPR:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li><strong>Contract Performance:</strong> Processing necessary to provide the Service you have subscribed to and to fulfill our contractual obligations to you (Article 6(1)(b)).</li>
              <li><strong>Consent:</strong> Where you have given explicit consent for specific processing activities, such as marketing communications (Article 6(1)(a)).</li>
              <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate business interests, such as improving our Service, preventing fraud, and ensuring security, provided these interests do not override your fundamental rights (Article 6(1)(f)).</li>
              <li><strong>Legal Obligations:</strong> Processing necessary to comply with legal obligations, such as tax and accounting requirements, or responding to lawful requests from authorities (Article 6(1)(c)).</li>
            </ul>
          </div>
        </section>

        <!-- 3. How We Use Your Information -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">3. How We Use Your Information</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>We use the information we collect for the following purposes:</p>
            <p class="mt-4"><strong>3.1 Service Provision:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>To create and manage your account</li>
              <li>To provide, operate, and maintain the Service</li>
              <li>To process transactions and send transaction notifications</li>
              <li>To enable features and functionality of the Service</li>
              <li>To store and manage your Customer Data</li>
            </ul>
            <p class="mt-4"><strong>3.2 Service Improvement:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>To understand how users interact with the Service</li>
              <li>To develop new features and functionality</li>
              <li>To improve user experience and Service performance</li>
              <li>To conduct analytics and research</li>
            </ul>
            <p class="mt-4"><strong>3.3 Communication:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>To send you technical notices, updates, and security alerts</li>
              <li>To respond to your inquiries and provide customer support</li>
              <li>To send administrative information about your account or subscription</li>
              <li>To send marketing communications (with your consent, and you may opt out at any time)</li>
            </ul>
            <p class="mt-4"><strong>3.4 Security and Fraud Prevention:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>To detect, prevent, and address technical issues</li>
              <li>To protect against fraudulent, unauthorized, or illegal activity</li>
              <li>To enforce our Terms of Service</li>
              <li>To monitor and analyze security threats</li>
            </ul>
            <p class="mt-4"><strong>3.5 Legal Compliance:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>To comply with applicable laws and regulations</li>
              <li>To respond to legal requests and prevent harm</li>
              <li>To maintain business records and accounting</li>
            </ul>
          </div>
        </section>

        <!-- 4. How We Share Your Information -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">4. How We Share Your Information</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
            <p class="mt-4"><strong>4.1 Service Providers:</strong></p>
            <p class="mt-2">We engage trusted third-party service providers to perform functions on our behalf, such as:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li><strong>Cloud Hosting:</strong> To host and store data</li>
              <li><strong>Payment Processing:</strong> Stripe for payment processing</li>
              <li><strong>Email Services:</strong> To send transactional and marketing emails</li>
              <li><strong>Analytics:</strong> To analyze usage patterns and improve the Service</li>
              <li><strong>Customer Support:</strong> To provide customer service and support</li>
            </ul>
            <p class="mt-4">These service providers have access to personal information only to perform specific tasks on our behalf and are obligated to protect your information and use it only for the purposes for which it was disclosed.</p>
            <p class="mt-4"><strong>4.2 Within Your Organization:</strong></p>
            <p class="mt-2">If you are using the Service as part of an organization, your Customer Data and usage information may be accessible to administrators and authorized users within your organization according to the access permissions configured.</p>
            <p class="mt-4"><strong>4.3 Business Transfers:</strong></p>
            <p class="mt-2">If we are involved in a merger, acquisition, sale of assets, or bankruptcy, your information may be transferred as part of that transaction. We will notify you of any such change and the choices you may have regarding your information.</p>
            <p class="mt-4"><strong>4.4 Legal Requirements:</strong></p>
            <p class="mt-2">We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, subpoenas, warrants) or to:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Comply with legal obligations</li>
              <li>Protect and defend our rights or property</li>
              <li>Prevent or investigate possible wrongdoing</li>
              <li>Protect the safety of users or the public</li>
              <li>Protect against legal liability</li>
            </ul>
            <p class="mt-4"><strong>4.5 With Your Consent:</strong></p>
            <p class="mt-2">We may share your information for any other purpose with your explicit consent.</p>
          </div>
        </section>

        <!-- 5. Data Retention -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">5. Data Retention</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.</p>
            <p class="mt-4"><strong>5.1 Active Accounts:</strong></p>
            <p class="mt-2">While your account is active, we retain your account information and Customer Data to provide the Service.</p>
            <p class="mt-4"><strong>5.2 After Account Termination:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Account information and Customer Data are typically deleted within 90 days of account termination</li>
              <li>Some information may be retained longer if required for legal, accounting, or regulatory purposes</li>
              <li>Backup copies may be retained for up to 180 days in accordance with our backup retention policies</li>
              <li>Anonymized and aggregated data may be retained indefinitely for analytics and research</li>
            </ul>
            <p class="mt-4"><strong>5.3 Legal and Business Records:</strong></p>
            <p class="mt-2">We retain financial records, transaction history, and communications for 7 years to comply with UK tax and accounting requirements.</p>
            <p class="mt-4"><strong>5.4 Your Right to Request Deletion:</strong></p>
            <p class="mt-2">You may request deletion of your personal data at any time, subject to legal retention requirements. See Section 6 for information about your rights.</p>
          </div>
        </section>

        <!-- 6. Your Data Protection Rights -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">6. Your Data Protection Rights (UK GDPR)</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>Under UK GDPR, you have the following rights regarding your personal data:</p>
            <p class="mt-4"><strong>6.1 Right of Access:</strong></p>
            <p class="mt-2">You have the right to request copies of your personal data. We may charge a reasonable fee if your request is clearly unfounded, repetitive, or excessive.</p>
            <p class="mt-4"><strong>6.2 Right to Rectification:</strong></p>
            <p class="mt-2">You have the right to request correction of any inaccurate personal data and to have incomplete personal data completed.</p>
            <p class="mt-4"><strong>6.3 Right to Erasure ("Right to be Forgotten"):</strong></p>
            <p class="mt-2">You have the right to request deletion of your personal data in certain circumstances, such as when:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>The data is no longer necessary for the purposes for which it was collected</li>
              <li>You withdraw consent and there is no other legal basis for processing</li>
              <li>You object to processing and there are no overriding legitimate grounds</li>
              <li>The data has been unlawfully processed</li>
            </ul>
            <p class="mt-4"><strong>6.4 Right to Restrict Processing:</strong></p>
            <p class="mt-2">You have the right to request that we restrict processing of your personal data in certain circumstances.</p>
            <p class="mt-4"><strong>6.5 Right to Data Portability:</strong></p>
            <p class="mt-2">You have the right to request transfer of your personal data to another organization or directly to you in a structured, commonly used, and machine-readable format.</p>
            <p class="mt-4"><strong>6.6 Right to Object:</strong></p>
            <p class="mt-2">You have the right to object to processing of your personal data based on legitimate interests or for direct marketing purposes.</p>
            <p class="mt-4"><strong>6.7 Rights Related to Automated Decision-Making:</strong></p>
            <p class="mt-2">You have the right not to be subject to decisions based solely on automated processing, including profiling, which produces legal effects or similarly significantly affects you.</p>
            <p class="mt-4"><strong>6.8 Right to Withdraw Consent:</strong></p>
            <p class="mt-2">Where processing is based on consent, you have the right to withdraw consent at any time. Withdrawal does not affect the lawfulness of processing before withdrawal.</p>
            <p class="mt-4"><strong>6.9 How to Exercise Your Rights:</strong></p>
            <p class="mt-2">To exercise any of these rights, please contact us at <a href="mailto:info@marginresolve.com" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginresolve.com</a>. We will respond to your request within one month, or within two months if the request is complex.</p>
            <p class="mt-4"><strong>6.10 Right to Lodge a Complaint:</strong></p>
            <p class="mt-2">You have the right to lodge a complaint with the Information Commissioner's Office (ICO), the UK supervisory authority for data protection issues:</p>
            <p class="mt-2">
              Information Commissioner's Office<br>
              Wycliffe House<br>
              Water Lane<br>
              Wilmslow<br>
              Cheshire SK9 5AF<br>
              Telephone: 0303 123 1113<br>
              Website: <a href="https://ico.org.uk" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline" target="_blank" rel="noopener noreferrer">https://ico.org.uk</a>
            </p>
          </div>
        </section>

        <!-- 7. Data Security -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">7. Data Security</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>We implement appropriate technical and organizational measures to protect your personal data against unauthorized or unlawful processing, accidental loss, destruction, or damage.</p>
            <p class="mt-4"><strong>7.1 Security Measures Include:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li><strong>Encryption:</strong> Data is encrypted in transit using TLS/SSL and at rest using industry-standard encryption</li>
              <li><strong>Access Controls:</strong> Strict access controls and authentication mechanisms, including two-factor authentication</li>
              <li><strong>Secure Infrastructure:</strong> Hosting on secure, certified cloud infrastructure with regular security audits</li>
              <li><strong>Regular Backups:</strong> Automated backups to prevent data loss</li>
              <li><strong>Security Monitoring:</strong> Continuous monitoring for security threats and vulnerabilities</li>
              <li><strong>Employee Training:</strong> Regular security training for employees with access to personal data</li>
              <li><strong>Incident Response:</strong> Procedures for detecting, responding to, and reporting security incidents</li>
            </ul>
            <p class="mt-4"><strong>7.2 Limitations:</strong></p>
            <p class="mt-2">While we strive to protect your personal data, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security. You are responsible for maintaining the confidentiality of your account credentials.</p>
            <p class="mt-4"><strong>7.3 Data Breach Notification:</strong></p>
            <p class="mt-2">In the event of a data breach that is likely to result in a risk to your rights and freedoms, we will notify you and the ICO within 72 hours of becoming aware of the breach, as required by UK GDPR.</p>
          </div>
        </section>

        <!-- 8. Cookies and Tracking Technologies -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">8. Cookies and Tracking Technologies</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p><strong>8.1 What Are Cookies:</strong></p>
            <p class="mt-2">Cookies are small text files placed on your device by websites you visit. They are widely used to make websites work more efficiently and provide information to website owners.</p>
            <p class="mt-4"><strong>8.2 How We Use Cookies:</strong></p>
            <p class="mt-2">We use the following types of cookies:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li><strong>Essential Cookies:</strong> Necessary for the Service to function properly, including authentication, security, and session management. These cannot be disabled.</li>
              <li><strong>Functional Cookies:</strong> Enable enhanced functionality and personalization, such as remembering your preferences and settings.</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with the Service by collecting and reporting information anonymously.</li>
              <li><strong>Performance Cookies:</strong> Collect information about how you use the Service to help us improve performance and user experience.</li>
            </ul>
            <p class="mt-4"><strong>8.3 Third-Party Cookies:</strong></p>
            <p class="mt-2">Some of our pages may contain content from third-party services (e.g., payment processors, analytics providers) that may set their own cookies. We do not control these third-party cookies.</p>
            <p class="mt-4"><strong>8.4 Managing Cookies:</strong></p>
            <p class="mt-2">Most web browsers allow you to control cookies through browser settings. However, if you disable essential cookies, some features of the Service may not function properly. You can:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Delete all cookies from your browser</li>
              <li>Block all cookies</li>
              <li>Allow all cookies</li>
              <li>Block third-party cookies</li>
              <li>Clear cookies when you close your browser</li>
            </ul>
            <p class="mt-4">To find out more about cookies, visit <a href="https://www.aboutcookies.org" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline" target="_blank" rel="noopener noreferrer">www.aboutcookies.org</a>.</p>
          </div>
        </section>

        <!-- 9. International Data Transfers -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">9. International Data Transfers</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>Our Service is operated in the United Kingdom. If you access the Service from outside the UK, your information may be transferred to, stored, and processed in the UK and other countries where our service providers are located.</p>
            <p class="mt-4">When we transfer personal data outside the UK to countries that do not provide an adequate level of protection as determined by the UK government, we implement appropriate safeguards, such as:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Standard Contractual Clauses approved by the UK authorities</li>
              <li>Binding Corporate Rules</li>
              <li>Transfers to countries with adequacy decisions</li>
              <li>Other legally approved mechanisms</li>
            </ul>
            <p class="mt-4">By using the Service, you consent to the transfer of your information to countries outside your country of residence, which may have different data protection rules.</p>
          </div>
        </section>

        <!-- 10. Children's Privacy -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">10. Children's Privacy</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>Our Service is not intended for children under the age of 18. We do not knowingly collect personal information from children under 18.</p>
            <p class="mt-4">If you are a parent or guardian and you become aware that your child has provided us with personal data without your consent, please contact us at <a href="mailto:info@marginresolve.com" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginresolve.com</a>. If we become aware that we have collected personal data from children without verification of parental consent, we will take steps to remove that information from our servers.</p>
          </div>
        </section>

        <!-- 11. Third-Party Links and Services -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">11. Third-Party Links and Services</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>Our Service may contain links to third-party websites, applications, or services that are not operated by us. This Privacy Policy does not apply to third-party services.</p>
            <p class="mt-4">We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services. We strongly advise you to review the privacy policy of every site you visit.</p>
            <p class="mt-4"><strong>Third-Party Services We Use:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li><strong>Stripe:</strong> Payment processing - <a href="https://stripe.com/privacy" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a></li>
              <li>Other third-party services as integrated into the Service</li>
            </ul>
          </div>
        </section>

        <!-- 12. Data Processing for Customers -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">12. Our Role as Data Processor</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>When you use our Service to process personal data of your customers, employees, or other third parties (Customer Data), you are the data controller and we act as the data processor on your behalf.</p>
            <p class="mt-4"><strong>12.1 Your Responsibilities as Data Controller:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>You are responsible for ensuring lawful processing of Customer Data</li>
              <li>You must have appropriate legal bases for processing personal data through our Service</li>
              <li>You must obtain necessary consents from data subjects</li>
              <li>You are responsible for responding to data subject rights requests</li>
              <li>You must have appropriate privacy policies and notices in place</li>
            </ul>
            <p class="mt-4"><strong>12.2 Our Commitments as Data Processor:</strong></p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>We process Customer Data only on your documented instructions</li>
              <li>We implement appropriate security measures to protect Customer Data</li>
              <li>We assist you in responding to data subject rights requests</li>
              <li>We notify you of any data breaches affecting Customer Data</li>
              <li>We delete or return Customer Data upon termination of the Service</li>
              <li>We use sub-processors only with your consent</li>
            </ul>
            <p class="mt-4"><strong>12.3 Data Processing Agreement:</strong></p>
            <p class="mt-2">Our data processing terms are incorporated into the Terms of Service and comply with UK GDPR requirements for data processing agreements.</p>
          </div>
        </section>

        <!-- 13. Marketing Communications -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">13. Marketing Communications</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>With your consent, we may send you marketing communications about our products, services, news, and offers that may be of interest to you.</p>
            <p class="mt-4"><strong>13.1 Opt-Out:</strong></p>
            <p class="mt-2">You can opt out of receiving marketing communications at any time by:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Clicking the "unsubscribe" link in any marketing email</li>
              <li>Updating your communication preferences in your account settings</li>
              <li>Contacting us at <a href="mailto:info@marginresolve.com" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginresolve.com</a></li>
            </ul>
            <p class="mt-4"><strong>13.2 Transactional Communications:</strong></p>
            <p class="mt-2">Please note that opting out of marketing communications does not affect transactional emails related to your account, such as password resets, billing notifications, and service announcements.</p>
          </div>
        </section>

        <!-- 14. Do Not Track Signals -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">14. Do Not Track Signals</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>Some web browsers have a "Do Not Track" feature that lets you tell websites you do not want to have your online activities tracked. Currently, there is no standard for how DNT signals should be interpreted, and our Service does not currently respond to DNT signals.</p>
          </div>
        </section>

        <!-- 15. Changes to This Privacy Policy -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">15. Changes to This Privacy Policy</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors.</p>
            <p class="mt-4"><strong>15.1 Notification of Changes:</strong></p>
            <p class="mt-2">We will notify you of any material changes by:</p>
            <ul class="list-disc pl-6 mt-2 space-y-2">
              <li>Updating the "Last Updated" date at the top of this policy</li>
              <li>Posting a notice on our website</li>
              <li>Sending you an email notification (for material changes)</li>
            </ul>
            <p class="mt-4"><strong>15.2 Your Continued Use:</strong></p>
            <p class="mt-2">Your continued use of the Service after any changes to this Privacy Policy constitutes your acceptance of the updated policy. We encourage you to review this Privacy Policy periodically.</p>
            <p class="mt-4"><strong>15.3 Previous Versions:</strong></p>
            <p class="mt-2">Previous versions of this Privacy Policy may be available upon request.</p>
          </div>
        </section>

        <!-- 16. Contact Information -->
        <section class="pb-6 border-b border-secondary-200 dark:border-secondary-700">
          <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-4">16. Contact Us</h2>
          <div class="text-secondary-700 dark:text-secondary-300 leading-relaxed">
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
            <p class="mt-4">
              <strong>Margin Solutions Ltd</strong><br>
              Data Protection Officer<br>
              Suite 3.10, 16 Robertson Street<br>
              Glasgow, Scotland<br>
              G2 8DS<br>
              United Kingdom
            </p>
            <p class="mt-4">
              Email (Margin Resolve): <a href="mailto:info@marginresolve.com" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginresolve.com</a><br>
              Email (General): <a href="mailto:info@marginsolutions.co.uk" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline">info@marginsolutions.co.uk</a><br>
              Company Registration: SC744893
            </p>
            <p class="mt-4">We will respond to your inquiry within 30 days.</p>
          </div>
        </section>

        <!-- Acknowledgment -->
        <section class="border-t border-secondary-200 dark:border-secondary-700 pt-8">
          <div class="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-6">
            <h3 class="text-lg font-semibold text-primary-900 dark:text-primary-100 mb-2">
              Acknowledgment
            </h3>
            <p class="text-primary-800 dark:text-primary-200">
              BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THIS PRIVACY POLICY, UNDERSTAND IT,
              AND AGREE TO ITS TERMS. IF YOU DO NOT AGREE TO THIS PRIVACY POLICY, YOU MUST NOT USE THE SERVICE.
            </p>
          </div>
        </section>
      </div>

      <!-- Footer -->
      <div class="mt-8 text-center text-sm text-secondary-500 dark:text-secondary-400">
        <p>© {{ currentYear }} Margin Solutions Ltd. All rights reserved.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import { ArrowLeftIcon } from '@heroicons/vue/24/outline';

// No layout for privacy page (full-page design)
defineOptions({
  layout: null,
});

const currentYear = computed(() => new Date().getFullYear());
</script>

<style scoped>
/* Custom styles without @apply to avoid Tailwind v4 issues */
</style>
